import { type Message as TMessage, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from '@/components/ui/hover-card';
import { cn } from '@/lib/utils';
import { isMac } from '@/utils/capabilities';

import {
  getCapturedAppIcon,
  useCapturedAppRuntimeIcons,
} from './capturedAppIcons';
import { collectTurnApps } from './collectTurnApps';

interface Props {
  message: TMessage;
  className?: string;
  variant?: 'pill' | 'plain';
}

const MAX_INLINE_ICONS = 3;

function PillIcon({ app, dataUrl }: { app: string; dataUrl: string | null }) {
  const { Icon, displayName } = getCapturedAppIcon(app);
  return (
    <span
      className="inline-flex size-5 items-center justify-center"
      aria-label={`Source: ${displayName}`}
    >
      {dataUrl ? (
        <img src={dataUrl} alt="" className="size-5" draggable={false} />
      ) : (
        <Icon className="size-5" />
      )}
    </span>
  );
}

function SourceRow({ app, dataUrl }: { app: string; dataUrl: string }) {
  return (
    <li className="flex items-center gap-2 rounded-md px-1 py-0.5 text-sm">
      <span className="inline-flex size-5 items-center justify-center shrink-0">
        <img src={dataUrl} alt="" className="size-5" draggable={false} />
      </span>
      <span className="text-foreground">{app}</span>
    </li>
  );
}

const hasElectronIconApi = (): boolean =>
  typeof window !== 'undefined' &&
  isMac() &&
  !!window.mainProcessApi?.getAppIconByName;

export const MessageSources = observer(function MessageSources({
  message,
  className,
  variant,
}: Props) {
  const { messages } = useStores();
  if (!hasElectronIconApi()) return null;

  const threadMessages = messages.getMessagesByThreadId(message.threadId);
  const apps = collectTurnApps(message, threadMessages);
  return <Inner apps={apps} className={className} variant={variant} />;
});

const Inner = observer(
  ({
    apps,
    className,
    variant = 'pill',
  }: {
    apps: string[];
    className?: string;
    variant?: 'pill' | 'plain';
  }) => {
    const states = useCapturedAppRuntimeIcons(apps);

    const resolved: { app: string; dataUrl: string }[] = [];
    const missing: string[] = [];
    for (const app of apps) {
      const state = states.get(app);
      if (state?.state === 'resolved' && state.dataUrl) {
        resolved.push({ app, dataUrl: state.dataUrl });
      } else if (state?.state === 'missing') {
        missing.push(app);
      }
    }

    const inline = resolved.slice(0, MAX_INLINE_ICONS);

    if (apps.length === 0) return null;
    const pillApps: string[] = inline.length
      ? inline.map((r) => r.app)
      : apps.slice(0, MAX_INLINE_ICONS);

    return (
      <HoverCard openDelay={250} closeDelay={120}>
        <HoverCardTrigger asChild>
          <span
            className={cn(
              'group inline-flex cursor-default items-center gap-2 outline-none focus-visible:outline-none',
              variant === 'pill' &&
                'rounded-full bg-surface-hover py-1 pl-2.5 pr-1.5 transition-colors hover:bg-surface-active',
              className
            )}
            data-testid="message-sources"
            tabIndex={-1}
          >
            <span className="text-xs leading-none text-foreground-muted">
              Sources
            </span>
            <span className="inline-flex items-center gap-0.5">
              {pillApps.map((app) => {
                const state = states.get(app);
                const dataUrl =
                  state?.state === 'resolved' ? state.dataUrl : null;
                return <PillIcon key={app} app={app} dataUrl={dataUrl} />;
              })}
            </span>
          </span>
        </HoverCardTrigger>
        <HoverCardContent
          side="top"
          align="center"
          sideOffset={8}
          className="w-52"
        >
          <div className="flex flex-col gap-2.5">
            <div className="text-sm font-medium text-foreground-muted">
              Sources
            </div>
            {resolved.length > 0 && (
              <ul className="flex flex-col gap-1">
                {resolved.map(({ app, dataUrl }) => (
                  <SourceRow key={app} app={app} dataUrl={dataUrl} />
                ))}
              </ul>
            )}
            {missing.length > 0 && (
              <p className="text-xs leading-snug text-foreground-muted">
                Also {missing.join(', ')}
              </p>
            )}
          </div>
        </HoverCardContent>
      </HoverCard>
    );
  }
);
